import {
    Property
} from "../../../../models";
import PropertyDAO, {
    ListPropertiesOptions
} from "../PropertyDAO";

import ClientLogger from "../../../logging/ClientLogger";
import { CreatePropertyError } from "../error/CreatePropertyError";
import { GetPropertyError } from "../error/GetPropertyError";
import PropertyMetricNames from "../../PropertyMetricNames";
import _ from "lodash";

export default class MockDataStorePropertyDAO implements PropertyDAO {
    private static readonly STORAGE_NAME = "mock-properties";
    private logger: ClientLogger;

    constructor(
        logger: ClientLogger
    ) {
        this.logger = logger;
    }

    private loadMock(): Property[] {
        let mock = new Array<Property>();
        const mockStr = localStorage.getItem(MockDataStorePropertyDAO.STORAGE_NAME);

        if (mockStr) {
            mock = JSON.parse(mockStr) as Property[];
        }

        return mock;
    }

    private saveMock(mock: Property[]): void {
        localStorage.setItem(MockDataStorePropertyDAO.STORAGE_NAME, JSON.stringify(mock));
    }

    public async getById(id: string): Promise<Property> {
        this.logger.info(
            `Getting Attachment Record: ${id}`,
            undefined,
            [PropertyMetricNames.GET_BY_ID_ATTEMPT]
        );
        try {
            const mock = this.loadMock();
            let result = mock.find((value): boolean => {
                return value.id === id;
            });

            if (!result) {
                throw new Error("Property not found");
            }

            return result;
        } catch (error) {
            const errorMessage = `Unable to get Property: ${id}`;
            this.logger.error(
                errorMessage,
                error,
                [PropertyMetricNames.GET_BY_ID_FAILURE]
            );
            throw new GetPropertyError(errorMessage, error as Error);
        }
    }

    public async create(
        property: Property
    ): Promise<Property> {
        try {
            this.logger.info(
                `Creating Property name: ${property.name}, address: ${property.address}`,
                undefined,
                [PropertyMetricNames.CREATE_ATTEMPT]
            );
            const propertyToCreate: Property = new Property({
                ...property,
                localLastUpdatedTime: Date.now()
            });
            const mock = this.loadMock();
            const exists = mock.find((value): boolean => {
                return value.id === propertyToCreate.id;
            });

            if (exists) {
                throw Error();
            }

            mock.push(propertyToCreate);
            this.saveMock(mock);

            return propertyToCreate;
        } catch (error) {
            const errorMessage = `Unable to create Property name: ${property.name}, address: ${property.address}`;
            this.logger.error(
                errorMessage,
                error,
                [PropertyMetricNames.CREATE_FAILURE]
            );
            throw new CreatePropertyError(errorMessage, error as Error);
        }
    }

    public async editProperty(
        propertyId: string,
        updatedProperty: Property
    ): Promise<Property> {
        try {
            const existingProperty: Property = await this.getById(propertyId);
            let result: Property = existingProperty;
            if (!_.isEqual(existingProperty, updatedProperty)) {
                result = {
                    ...result,
                    name: updatedProperty.name,
                    description: updatedProperty.description,
                    address: updatedProperty.address,
                    primaryContactName: updatedProperty.primaryContactName,
                    primaryContactNumber: updatedProperty.primaryContactNumber,
                    primaryContactEmail: updatedProperty.primaryContactEmail,
                    localLastUpdatedTime: Date.now(),
                    autoDashboardRebuild: updatedProperty.autoDashboardRebuild,
                    isArchived: updatedProperty.isArchived
                };
            }

            const mock = this.loadMock();
            const index = mock.findIndex((value): boolean => {
                return value.id === result.id;
            });

            if (index >= 0) {
                mock[index] = result;

                this.saveMock(mock);
            }

            return result;
        } catch (error) {
            throw new Error("Failed to update the property");
        }
    }

    public async listProperties(
        options?: ListPropertiesOptions
    ): Promise<Array<Property>> {
        const mock = this.loadMock();
        const limit = options?.limit;
        const shouldIncludeArchived = options?.shouldIncludeArchived || false;

        return mock.filter((property): boolean => {
            if (shouldIncludeArchived) {
                return true;
            } else {
                return !property.isArchived;
            }
        }).sort((left, right): number => {
            return (right.localLastUpdatedTime ?? 0) - (left.localLastUpdatedTime ?? 0);    // descending
        }).slice(0, limit);
    }
}
